import React, { useRef, useState } from "react";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import {
  DialogTextContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  List,
  Tabs,
  Text,
  Icon,
  isNullish
} from "@theorgchartdev/storybook";
import * as StateLib from "@/Utils/StateLib";
import FileDirectory from "../../Composites/FileDirectory/FileDirectory";
import cx from "classnames";
import { useDirectorySearchContext } from "@/providers/DirectorySearchProvider";
import { GetUserList } from "@/Utils/Promises";
import { GetRecentDocsFromUser } from "@/Utils/DataLib";
import PlanExplorer from "./PlanExplorer/PlanExplorer";
import {
  kHeadcount,
  kBudgetedHeadcount,
  kSalaryRollup,
  kPositionBudgetRollup,
  kPositionCount
} from "@coreapp/constants";
import PlanBuilderModal from "../PlanBuilderModal/PlanBuilderModal";
import { PointInTimeExplorer } from "./PointInTimeExplorer/PointInTimeExplorer";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import CentralizedModalContainer from "../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QBuildPlanRFC } from "@coreapp/CoreApp/QBuildPlanRFC";
import { appstr } from "@coreapp/utils/appstr";
import { enumFeaturesList } from "@coreapp/constants";
import { getFeatureAccess } from "@/Utils/Misc";
import UpgradePIT from "./PointInTimeExplorer/UpgradePIT";
import UpgradePlanning from "./PlanExplorer/UpgradePlanning";
import UpgradeTabHeader from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeTabHeader";

const enumTabs = { recents: 0, basecharts: 1, files: 2, plans: 3, pit: 4 };

const ChartExplorerModal = props => {
  let wsMgr = StateLib.GetWSMgr();
  let doc = StateLib.GetDoc();
  let { setDirSearchState, ResetColumnNames } = useDirectorySearchContext();
  let isPlan = doc?.IsPlanChart();

  const {
    chartExplorer: { closeChartExplorer, showChartExplorer, explorerLaunchTab },
    planning: { OpenPlanChart }
  } = useNavigationContext();

  const nav = useAppNavigate();
  let currentOpenChartID = doc?.IsServerChart() ? doc.GetChartID() : null;
  let currentOpenFilename = wsMgr ? wsMgr.GetFileName(true) : null;
  const [userDocuments, setUserDocuments] = React.useState(null);
  const [selectedDocument, setSelectedDocument] = React.useState(null);
  const [selectedBaseChart, setSelectedBaseChart] = React.useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPointInTime, setSelectedPointInTime] = useState(null);
  const pointInTimeExplorerRef = useRef(null);

  const [showBuilder, setShowBuilder] = React.useState(false);

  const {
    canAccessPIT,
    canAccessPlanning,
    canAccessFM,
    showPricingAndPackaging,
    canAccessChartExplorer
  } = usePermissionCheck();

  const ClosePlanBuilder = () => {
    setShowBuilder(false);
  };

  const OpenPlanBuilder = () => {
    setShowBuilder(true);
  };

  const getStartTab = () => {
    let tabIdx = localStorage.getItem("chart-explorer-section") ?? 0;
    if (explorerLaunchTab) tabIdx = explorerLaunchTab;
    const parsedTabIdx = parseInt(tabIdx);
    if (isNaN(parsedTabIdx) || !Object.values(enumTabs).includes(parsedTabIdx)) {
      return 0;
    }
    return parsedTabIdx;
  };

  const [currentTab, setCurrentTab] = React.useState(getStartTab());

  let showFavorites = false;

  const LoadRecentDocs = async () => {
    const { userList } = await GetUserList(true);
    const docs = GetRecentDocsFromUser(userList[0]);
    setUserDocuments(docs); // contains temp and recents (filtering temps currently)
  };

  React.useEffect(() => {
    if (!showChartExplorer) {
      setUserDocuments(null);
      return;
    }
    // Only load recent docs when opening recents tab and if not previously loadeds
    if (currentTab === enumTabs.recents && userDocuments === null) {
      LoadRecentDocs();
    }
  }, [showChartExplorer, currentTab, userDocuments]);

  if (!showChartExplorer || (!Globals._uiConfig.fChartExplorer && showChartExplorer)) return;

  const OpenRecentCustomChart = () => {
    const recentDoc = selectedDocument;
    if (!recentDoc) return;

    const wsMgr = StateLib.GetWSMgr();
    const theFileName = recentDoc.filename;
    const theDirectory = recentDoc.directory;
    const extension = recentDoc.extension;
    const fullName = theFileName + "." + extension;
    localStorage.FMLastDirectory = theDirectory;

    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("chart_mode.custom_chart.opened");

    setDirSearchState(prevState => {
      return { ...prevState, results: [] };
    });
    ResetColumnNames();

    nav.toCustomChart(theDirectory, fullName);
  };

  const OpenBaseChart = () => {
    if (!selectedBaseChart) return;
    const wsMgr = StateLib.GetWSMgr();

    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("chart_mode.master_chart.opened");

    setDirSearchState(prevState => {
      return { ...prevState, results: [] };
    });
    ResetColumnNames(selectedBaseChart);

    nav.toChart(selectedBaseChart);
  };

  const LaunchPlan = () => {
    if (selectedPlan == null) return;
    OpenPlanChart(selectedPlan);
  };

  const onFileManagerClick = fileObj => {
    if (fileObj?.IsChartDocument() || fileObj?.IsBackground()) {
      let doc = {};
      doc.filename = fileObj.GetFileName();
      doc.directory = fileObj.GetParentFolder().GetFolderName();
      doc.extension = fileObj.GetFileExt();
      setSelectedDocument(doc);
    } else {
      clearSelections();
    }
  };

  const onFileManagerSelect = fileObj => {
    onFileManagerClick(fileObj);
    OpenRecentCustomChart();
  };

  const handleDocListClick = (option, e) => {
    if (e.type === "click") {
      switch (e.detail) {
        case 1:
          setSelectedDocument(option.doc);
          break;
        case 2:
          setSelectedDocument(option.doc);
          OpenRecentCustomChart();
          break;
        default:
      }
    } else if (e.type === "keydown") {
      setSelectedDocument(option.doc);
    }
  };

  const handleBaseChartListClick = (option, e) => {
    if (e.type === "click") {
      switch (e.detail) {
        case 1:
          setSelectedBaseChart(option.value);
          break;
        case 2:
          setSelectedBaseChart(option.value);
          OpenBaseChart(option);
          break;
        default:
      }
    } else if (e.type === "keydown") {
      setSelectedBaseChart(option.value);
    }
  };

  const handlePlanChartListClick = (option, e) => {
    if (e.type === "click") {
      switch (e.detail) {
        case 1:
          setSelectedPlan(option);
          break;
        case 2:
          setSelectedPlan(option);
          LaunchPlan(option);
          break;
        default:
      }
    } else if (e.type === "keydown") {
      setSelectedPlan(option);
    }
  };

  const handlePointInTimeClick = (e, data) => {
    if (e.type === "click") {
      switch (e.detail) {
        case 1:
          setSelectedPointInTime(data);
          break;
        case 2:
          setSelectedPointInTime(data);
          pointInTimeExplorerRef.current?.OpenPointInTimeBuilder(data);
          break;
        default:
      }
    } else if (e.type === "keydown") {
      setSelectedPointInTime(data);
    }
  };

  const dataTestIdPrefix = "chart-exp-tab-";

  let tabs = [];
  let recentTab = {};
  let recentCustomChartOptions = BuildDocList(
    userDocuments,
    "recents",
    handleDocListClick,
    currentOpenFilename,
    selectedDocument
  );
  recentTab.header = appstr("Recents");
  recentTab.content = (
    <div className={cx("ocn-chart-explorer-list-container", "ocn-list-scrollbar")}>
      <List
        enableVirtualization={false}
        className={"ocn-tabs-recent-content"}
        value={GetUniqueDocKey(selectedDocument)}
        listWidth="fill"
        options={recentCustomChartOptions}
        coloredSelection={true}
      />
    </div>
  );
  recentTab.dataTestId = `${dataTestIdPrefix}${"recents"}`;
  recentTab.disabled = !canAccessChartExplorer;
  tabs.push(recentTab);

  let sharedTab = {};
  let baseChartOptions = BuildBaseChartOptions(
    handleBaseChartListClick,
    currentOpenChartID,
    selectedBaseChart
  );
  sharedTab.header = appstr("Base Charts");
  sharedTab.content = (
    <React.Fragment key="base">
      <div className={cx("ocn-chart-explorer-list-container", "ocn-list-scrollbar")}>
        <List
          enableVirtualization={false}
          className={"ocn-tabs-base-content"}
          value={selectedBaseChart}
          listWidth="fill"
          options={baseChartOptions}
          coloredSelection={true}
        />
      </div>
    </React.Fragment>
  );
  sharedTab.dataTestId = `${dataTestIdPrefix}${"basecharts"}`;
  sharedTab.disabled = !canAccessChartExplorer;
  tabs.push(sharedTab);

  let myFiles = {};
  myFiles.header = appstr("File Manager");
  myFiles.content = (
    <FileDirectory
      onFileClick={onFileManagerClick}
      onFileSelect={onFileManagerSelect}
      dataTestIdPrefix="chart-mode-explorer-files"
    />
  );
  myFiles.dataTestId = `${dataTestIdPrefix}${"filemgr"}`;
  myFiles.disabled = !canAccessFM;
  tabs.push(myFiles);

  const createPlan = formData => {
    const curUser = wsMgr.GetUserName();
    const doc = StateLib.GetDoc();

    const chartID = doc.IsServerChart() ? doc.GetChartID() : doc.GetIWizData().GetBaseChartID();
    const activeTemplateID = doc.GetActiveTemplate()?.GetTemplateID();

    const builderInfo = {};
    builderInfo["name"] = formData.name;
    builderInfo["description"] = formData.description;
    builderInfo["levels"] = formData.levels;
    builderInfo["topBoxID"] = formData.topbox.topBoxID.toString();
    builderInfo["objective"] = formData.objective;
    builderInfo["users"] = formData.sharedWith.map(user => ({
      name: user.username,
      role: user.editorRole
    }));
    builderInfo["status"] = "Active";
    builderInfo["chartID"] = chartID; //TODO: needs to be more sophisticated
    builderInfo["owner"] = curUser;

    const isPositionChart = doc.IsPositionDocument();
    if (isPositionChart) {
      builderInfo["metrics"] = [
        { fieldID: kPositionCount, targetValue: "" },
        { fieldID: kBudgetedHeadcount, targetValue: "" },
        { fieldID: kSalaryRollup, targetValue: "" },
        { fieldID: kPositionBudgetRollup, targetValue: "" }
      ];
    } else {
      builderInfo["metrics"] = [{ fieldID: kHeadcount, targetValue: "" }];
    }

    wsMgr.fBuilderInfo = { doctype: "plan", viewid: activeTemplateID };

    const accountName = wsMgr.GetAccountName();
    const newPlanRFC = new QBuildPlanRFC();
    newPlanRFC.InitUpdatePlans(wsMgr);
    newPlanRFC.fIsPositionChart = isPositionChart;
    newPlanRFC.SetParams(
      accountName,
      curUser,
      "newplan",
      JSON.stringify(builderInfo),
      formData.emailInvite
    );
    Globals._rfcMgr.QueueRFC(newPlanRFC);

    ClosePlanBuilder();
    closeChartExplorer();
  };

  const GetQueryChartID = () => {
    const doc = StateLib.GetDoc();
    return doc.IsServerChart() ? null : doc?.GetIWizData()?.GetBaseChartID();
  };

  let plans = {};
  plans.header = appstr("Plans");
  if (getFeatureAccess(enumFeaturesList.planning) || !showPricingAndPackaging) {
    plans.content = (
      <React.Fragment>
        <PlanExplorer
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          onClose={closeChartExplorer}
          onPlanClick={handlePlanChartListClick}
          openPlanBuilder={OpenPlanBuilder}
        />
        {showBuilder && (
          <PlanBuilderModal
            show={showBuilder}
            onClose={ClosePlanBuilder}
            onCreate={createPlan}
            queryID={GetQueryChartID()}
          />
        )}
      </React.Fragment>
    );
  } else {
    plans.header = (
      <UpgradeTabHeader label={appstr("Plans")} dataTestIdPrefix={`${dataTestIdPrefix}plans`} />
    );
    plans.content = <UpgradePlanning />;
  }
  plans.dataTestId = `${dataTestIdPrefix}${"plans"}`;

  if (
    canAccessPlanning ||
    !showPricingAndPackaging ||
    !getFeatureAccess(enumFeaturesList.planning)
  ) {
    tabs.push(plans);
  }

  let pointInTime = {};
  pointInTime.header = appstr("Point In Time");
  if (getFeatureAccess(enumFeaturesList.PIT) || !showPricingAndPackaging) {
    pointInTime.content = (
      <PointInTimeExplorer
        handleRef={pointInTimeExplorerRef}
        onPointInTimeClick={handlePointInTimeClick}
        selectedPointInTime={selectedPointInTime}
        setSelectedPointInTime={setSelectedPointInTime}
      />
    );
  } else {
    pointInTime.header = (
      <UpgradeTabHeader
        label={appstr("Point In Time")}
        dataTestIdPrefix={`${dataTestIdPrefix}pit`}
      />
    );
    pointInTime.content = <UpgradePIT />;
  }
  pointInTime.dataTestId = `${dataTestIdPrefix}${"pit"}`;
  if (canAccessPIT) {
    tabs.push(pointInTime);
  }

  const handleTabChange = currentTabIndex => {
    setCurrentTab(currentTabIndex);
    localStorage.setItem("chart-explorer-section", currentTabIndex);
    clearSelections();
  };

  const clearSelections = () => {
    setSelectedDocument(null);
    setSelectedBaseChart(null);
  };

  const inPlanExplorer = currentTab == enumTabs.plans;

  return (
    <CentralizedModalContainer
      show={showChartExplorer}
      errorBoundaryProps={{ onError: closeChartExplorer }}
    >
      <Modal
        {...props}
        id="example-modal"
        data-testid="chart-explorer-modal"
        show={showChartExplorer}
        centerScreen={true}
        onClose={closeChartExplorer}
        fullScreen={true}
        style={{ padding: 0 }}
      >
        <DialogTitle divider={true} onClose={closeChartExplorer}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"compass"} />
            {appstr("Chart Explorer")}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"ocn-chart-explorer-content"}>
          <div className="ocn-chart-explorer">
            {showFavorites && (
              <div className={cx("ocn-chart-explorer-column", "ocn-chart-explorer-starred")}>
                <DialogTextContent modifier={"medium"} size={"xsmall"} type={"display"}>
                  {appstr("Favorites")}
                  {GetEmptyContent()}
                </DialogTextContent>
              </div>
            )}
            <div className={cx("ocn-chart-explorer-column", "ocn-chart-explorer-tabular")}>
              <Tabs
                tabIndex={parseInt(currentTab)}
                customClass={"ocn-chart-explorer-tabs"}
                customContentClass={"ocn-chart-explorer-tabs-content"}
                tabs={tabs}
                onTabChange={handleTabChange}
                tablistProps={{
                  "aria-label": `${appstr("Chart Explorer")} ${appstr("Options").toLowerCase()}`
                }}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions divider={true} size="medium">
          <ChartExplorerActions
            state={{
              currentTab,
              selectedBaseChart,
              selectedDocument,
              selectedPlan,
              selectedPointInTime
            }}
            handlers={{
              OpenRecentCustomChart,
              OpenBaseChart,
              LaunchPlan,
              closeChartExplorer,
              OpenPointInTimeBuilder: pointInTimeExplorerRef.current?.OpenPointInTimeBuilder
            }}
          />
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

const ChartExplorerActions = props => {
  let primaryLabelKey = "Open";
  let primaryClickHandler = null;
  let primaryDisabled = true;

  let tertiaryLabelKey = "notset";
  let tertiaryClickHandler = null;
  let tertiaryDisabled = true;

  switch (parseInt(props.state.currentTab)) {
    default:
      primaryClickHandler = props.handlers.OpenRecentCustomChart;
      primaryDisabled = props.state.selectedDocument == null;
      break;
    // Base Charts
    case 1:
      primaryClickHandler = props.handlers.OpenBaseChart;
      primaryDisabled = props.state.selectedBaseChart == null;
      break;
    // Plans
    case enumTabs.plans:
      primaryClickHandler = props.handlers.LaunchPlan;
      primaryDisabled = props.state.selectedPlan == null;
      tertiaryLabelKey = "Cancel";
      tertiaryClickHandler = props.handlers.closeChartExplorer;
      tertiaryDisabled = false;
      break;
    // Point In Time
    case 4:
      primaryLabelKey = "Build";
      primaryClickHandler = () => {
        props.handlers.OpenPointInTimeBuilder?.(props.state.selectedPointInTime);
      };
      primaryDisabled = props.state.selectedPointInTime == null;
      break;
  }

  const isPlan = props.state.selectedPlan != null;

  return (
    <>
      {!isNullish(tertiaryClickHandler) ? (
        <DialogActions.Tertiary
          disabled={tertiaryDisabled}
          onClick={tertiaryClickHandler}
          dataTestId={`chart-exp-action-${tertiaryLabelKey}`}
          size="medium"
        >
          {appstr(tertiaryLabelKey)}
        </DialogActions.Tertiary>
      ) : null}
      <DialogActions.Primary
        disabled={primaryDisabled}
        onClick={primaryClickHandler}
        dataTestId={`chart-exp-action-${primaryLabelKey}`}
        size="medium"
      >
        {appstr(primaryLabelKey)}
      </DialogActions.Primary>
    </>
  );
};

const GetEmptyContent = padding => {
  return (
    <div style={{ padding: parseInt(padding) }}>
      <DialogTextContent size={"small"} colorModifier={"lightgrey"} type={"body"}>
        {appstr("No files or folders here")}
      </DialogTextContent>
    </div>
  );
};

const BuildDocList = (docs, key, clickHandler, currentDocName, selectedDoc) => {
  let options = [];
  if (!docs?.[key]) return options;

  for (let doc of docs[key]) {
    let isBackground = doc.extension.includes("oc5m") || doc.extension.includes("ocb");
    let iconProps = { name: isBackground ? "file-chart-pie" : "diagram-project" };
    let baseClass = "ocn-file-directory-icon";
    let colorClass = doc.extension.includes("oc5") ? `${baseClass}--yellow` : null;
    let isSelected = doc === selectedDoc;
    let activeClass = isSelected ? "ocn-file-row-active" : colorClass;
    let isOpen = currentDocName && doc.filename + "." + doc.extension == currentDocName;
    let chartOption = {};
    chartOption.content = (
      <span style={{ display: "flex", justifyContent: "space-between", padding: "4px 24px" }}>
        <span className={cx(baseClass, activeClass)}>
          <Icon {...iconProps} />
          <Text size={"small"}>{doc.filename}</Text>
        </span>
        {isOpen ? GetCurrentOpenChartContent(false, selectedDoc === doc) : null}
      </span>
    );
    chartOption.label = doc.filename;
    chartOption.doc = doc;
    chartOption.value = GetUniqueDocKey(doc);
    chartOption.clickHandler = clickHandler;
    chartOption.divider = true;
    options.push(chartOption);
  }
  return options;
};

const GetUniqueDocKey = doc => {
  if (!doc) {
    return;
  }

  return `${doc.directory}.${doc.filename}`;
};

const GetCurrentOpenChartContent = (label, selected) => {
  let baseClass = "ocn-chart-explorer-active-button-wrapper";
  let activeClass = "ocn-chart-explorer-active";
  let currentIconTextClass = "ocn-chart-explorer-current-text-wrapper";
  let selectedClass = selected ? activeClass : null;
  let modifier = selected ? "bold" : null;
  return (
    <React.Fragment>
      <span className={cx(baseClass, selectedClass)}>
        {label && (
          <Text size={"small"} modifier={modifier}>
            {label}
          </Text>
        )}
        <span className={cx(currentIconTextClass, selectedClass)}>
          <Icon size={"small"} name={"eye"} />
          <Text size={"small"}>{appstr("Currently Viewing")}</Text>
        </span>
      </span>
    </React.Fragment>
  );
};

const BuildBaseChartOptions = (clickHandler, currentID, selectedValue) => {
  var wsMgr = StateLib.GetWSMgr();
  let charts = wsMgr ? wsMgr.GetAllowedCharts() : [];
  let doc = StateLib.GetDoc();
  let baseClass = "ocn-file-directory-icon";
  let iconProps = { name: "sitemap" };
  return charts.map((item, index) => {
    let isSelected = item["ID"] === selectedValue;
    let activeClass = isSelected ? "ocn-file-row-active" : null;
    let baseChartName = item["name"];
    let isOpen = doc && doc.IsServerChart() && currentID == item["ID"];
    return {
      label: baseChartName,
      content: (
        <span style={{ display: "flex", justifyContent: "space-between", padding: "4px 24px" }}>
          <span className={cx(baseClass, activeClass)}>
            <Icon {...iconProps} />
            <Text size={"small"}>{baseChartName}</Text>
          </span>
          {isOpen ? GetCurrentOpenChartContent(false, selectedValue === item["ID"]) : null}
        </span>
      ),
      value: item["ID"],
      clickHandler: clickHandler,
      divider: true
    };
  });
};

export default ChartExplorerModal;
